import type {
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { cn, WeightGlyph } from '@/components/ui';
import type { WeightStatus } from '@/logic/weights';
import type { PlacedLabel } from './labels';
import type { MapPoint } from './mapModel';

export interface ProjectedPoint extends MapPoint {
  x: number;
  y: number;
}

/** Hit radius around each point; larger than the marker for easy targeting. */
export const HIT_RADIUS = 12;
/** Rendered weight-glyph size in pixels. */
const GLYPH_SIZE = 14;

const categoryText = (point: MapPoint) =>
  point.system === 1 ? 'text-record' : 'text-field';
const categoryStroke = (point: MapPoint) =>
  point.system === 1 ? 'stroke-record' : 'stroke-field';

/** Weight by shape (the design-system WeightGlyph), category by color. */
export const PointMarker = ({ point }: { point: ProjectedPoint }) => {
  const { x, y } = point;
  if (point.kind === 'unmatched') {
    return (
      <rect
        x={x - 3.5}
        y={y - 3.5}
        width={7}
        height={7}
        rx={1}
        className={cn('fill-surface-sunken opacity-75', categoryStroke(point))}
        strokeWidth={1.25}
      />
    );
  }
  if (point.kind === 'transformed') {
    return (
      <circle
        cx={x}
        cy={y}
        r={3.25}
        className={cn(
          'fill-record stroke-surface-sunken',
          point.status === 'ignore' && 'opacity-50'
        )}
        strokeWidth={1.5}
      />
    );
  }
  const status: WeightStatus = point.status ?? 'unity';
  return (
    <>
      <circle cx={x} cy={y} r={6.5} className="fill-surface-sunken" />
      <WeightGlyph
        status={status}
        size={GLYPH_SIZE}
        x={x - GLYPH_SIZE / 2}
        y={y - GLYPH_SIZE / 2}
        overflow="visible"
        className={cn(categoryText(point), status === 'ignore' && 'opacity-60')}
      />
    </>
  );
};

interface PointLayerProps {
  points: readonly ProjectedPoint[];
  labels: readonly PlacedLabel[];
  selectedPairId: number | null;
  hoveredKey: string | null;
  linkSourceKey: string | null;
  tabStop: string | null;
  /** Hidden description ids: one per weight status and one for flags. */
  describedBy: (point: MapPoint) => string | undefined;
  onActivate: (point: MapPoint) => void;
  onOpenMenu: (
    point: MapPoint,
    position: { x: number; y: number },
    element: Element
  ) => void;
  onHover: (
    point: MapPoint | null,
    position?: { x: number; y: number }
  ) => void;
  onFocusPoint: (point: MapPoint, element: Element) => void;
  onKeyDown: (event: ReactKeyboardEvent, point: MapPoint) => void;
}

/** Markers, emphasis rings, decluttered labels, and focusable hit targets. */
export const PointLayer = ({
  points,
  labels,
  selectedPairId,
  hoveredKey,
  linkSourceKey,
  tabStop,
  describedBy,
  onActivate,
  onOpenMenu,
  onHover,
  onFocusPoint,
  onKeyDown,
}: PointLayerProps) => {
  const byKey = new Map(points.map((point) => [point.key, point]));
  const isSelected = (point: MapPoint) =>
    point.kind !== 'unmatched' &&
    selectedPairId !== null &&
    point.pairId === selectedPairId;

  const emphasisOf = (
    point: MapPoint
  ): 'selected' | 'link' | 'hovered' | null => {
    if (linkSourceKey === point.key) return 'link';
    if (isSelected(point)) {
      // The Field point carries the halo; its transformed Record sits inside.
      return point.kind === 'transformed' ? null : 'selected';
    }
    return hoveredKey === point.key ? 'hovered' : null;
  };

  const handleKeyDown = (event: ReactKeyboardEvent, point: MapPoint) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate(point);
      return;
    }
    if (
      point.kind !== 'unmatched' &&
      (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10'))
    ) {
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      onOpenMenu(
        point,
        { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
        event.currentTarget
      );
      return;
    }
    onKeyDown(event, point);
  };

  const handleFocus = (event: ReactFocusEvent, point: MapPoint) => {
    onFocusPoint(point, event.currentTarget);
    const rect = event.currentTarget.getBoundingClientRect();
    onHover(point, { x: rect.right, y: rect.bottom });
  };

  return (
    <>
      {/* Halo fills sit under the markers; their rings draw above them so
          neighboring markers cannot hide a selection or flag. */}
      <g aria-hidden="true" className="pointer-events-none">
        {points.map((point) => {
          const emphasis = emphasisOf(point);
          if (!emphasis) return null;
          return (
            <circle
              key={point.key}
              cx={point.x}
              cy={point.y}
              r={emphasis === 'hovered' ? 11 : 13}
              className={
                emphasis === 'hovered' ? 'fill-fg/5' : 'fill-accent/15'
              }
            />
          );
        })}
      </g>

      <g className="pointer-events-none">
        {points.map((point) => (
          <g key={point.key}>
            <PointMarker point={point} />
          </g>
        ))}
      </g>

      <g aria-hidden="true" className="pointer-events-none fill-none">
        {points.map((point) => {
          const emphasis = emphasisOf(point);
          if (!emphasis && !point.flagged) return null;
          const { x, y } = point;
          return (
            <g key={point.key}>
              {emphasis === 'hovered' && (
                <circle
                  cx={x}
                  cy={y}
                  r={11}
                  className="stroke-fg-muted/60"
                  strokeWidth={1}
                />
              )}
              {emphasis === 'selected' && (
                <circle
                  data-selected-key={point.key}
                  cx={x}
                  cy={y}
                  r={13}
                  className="stroke-accent"
                  strokeWidth={1.75}
                />
              )}
              {emphasis === 'link' && (
                <circle
                  cx={x}
                  cy={y}
                  r={13}
                  className="stroke-accent"
                  strokeWidth={1.75}
                  strokeDasharray="4 2.5"
                />
              )}
              {point.flagged && (
                <circle
                  data-flagged-id={point.pairId ?? undefined}
                  cx={x}
                  cy={y}
                  r={9}
                  className="stroke-danger"
                  strokeWidth={2}
                >
                  <title>Flagged for review</title>
                </circle>
              )}
            </g>
          );
        })}
      </g>

      <g aria-hidden="true" className="pointer-events-none select-none">
        {labels.map((label) => {
          const point = byKey.get(label.key);
          const emphasized =
            !!point &&
            (isSelected(point) ||
              hoveredKey === point.key ||
              linkSourceKey === point.key);
          return (
            <text
              key={label.key}
              x={label.x}
              y={label.y}
              textAnchor={label.anchor}
              className={cn(
                'text-[11px] [paint-order:stroke] stroke-surface-sunken',
                point?.kind === 'unmatched' ? 'fill-fg-muted' : 'fill-fg',
                emphasized ? 'font-semibold' : 'font-medium',
                point?.flagged && 'fill-danger-fg'
              )}
              strokeWidth={3}
              strokeLinejoin="round"
            >
              {label.text}
            </text>
          );
        })}
      </g>

      <g>
        {points.map((point) => {
          const selected = isSelected(point);
          return (
            <g key={point.key}>
              <circle
                data-map-point="true"
                data-point-key={point.key}
                cx={point.x}
                cy={point.y}
                r={HIT_RADIUS}
                role="button"
                tabIndex={tabStop === point.key ? 0 : -1}
                aria-label={point.ariaLabel}
                aria-pressed={point.kind === 'unmatched' ? undefined : selected}
                aria-describedby={describedBy(point)}
                className="peer cursor-pointer fill-transparent outline-none"
                onClick={(event) => {
                  event.stopPropagation();
                  onActivate(point);
                }}
                onContextMenu={(event: ReactMouseEvent) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (point.kind === 'unmatched') return;
                  onOpenMenu(
                    point,
                    { x: event.clientX, y: event.clientY },
                    event.currentTarget
                  );
                }}
                onMouseEnter={(event) =>
                  onHover(point, { x: event.clientX, y: event.clientY })
                }
                onMouseLeave={() => onHover(null)}
                onFocus={(event) => handleFocus(event, point)}
                onBlur={() => onHover(null)}
                onKeyDown={(event) => handleKeyDown(event, point)}
              />
              <circle
                aria-hidden="true"
                cx={point.x}
                cy={point.y}
                r={HIT_RADIUS + 2}
                className="pointer-events-none fill-none stroke-focus opacity-0 peer-focus-visible:opacity-100"
                strokeWidth={2}
              />
            </g>
          );
        })}
      </g>
    </>
  );
};
